import { memo, useState } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  AlertCircle,
  Info,
  Loader2,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import type { SecurityScanResult, ResourceSecurityResult, SecurityIssue } from '../types';
import clsx from 'clsx';

interface SecurityPanelProps {
  className?: string;
  scanResult: SecurityScanResult | null;
  isScanning: boolean;
  onScan: () => void;
  hasManifests: boolean;
  onNavigateToResource?: (fileName: string) => void;
}

function SecurityPanelComponent({
  className,
  scanResult,
  isScanning,
  onScan,
  hasManifests,
  onNavigateToResource,
}: SecurityPanelProps) {
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());

  const toggleResource = (key: string) => {
    setExpandedResources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const getResourceStatus = (resource: ResourceSecurityResult) => {
    const issues = resource.issues || [];
    const hasErrors = issues.some(i => i.severity === 'error');
    const hasWarnings = issues.some(i => i.severity === 'warning');

    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'passed';
  };

  const renderIssue = (issue: SecurityIssue, index: number) => {
    const isError = issue.severity === 'error';

    return (
      <div
        key={index}
        className={clsx(
          'rounded px-2 py-1.5 text-[11px]',
          isError ? 'bg-red-500/5' : 'bg-yellow-500/5'
        )}
      >
        <div className="flex items-start gap-2">
          {isError ? (
            <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={clsx(
                'font-medium',
                isError ? 'text-red-400' : 'text-yellow-400'
              )}>
                {issue.checkName}
              </span>
              <span className={clsx(
                'text-[9px] px-1 py-0.5 rounded uppercase',
                isError ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
              )}>
                {issue.severity}
              </span>
            </div>
            <p className="text-dark-300 mt-0.5">{issue.description}</p>
            {issue.remediation && (
              <p className="text-dark-500 mt-1 text-[10px]">
                <span className="text-dark-400">Fix: </span>
                {issue.remediation}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleNavigateToResource = (e: React.MouseEvent, fileName: string) => {
    e.stopPropagation();
    if (onNavigateToResource) {
      onNavigateToResource(fileName);
    }
  };

  const renderResource = (resource: ResourceSecurityResult, index: number) => {
    const resourceKey = `${resource.fileName}-${resource.objectName}-${index}`;
    const isExpanded = expandedResources.has(resourceKey);
    const status = getResourceStatus(resource);
    const issueCount = resource.issues?.length || 0;
    const errorCount = resource.issues?.filter(i => i.severity === 'error').length || 0;
    const warningCount = resource.issues?.filter(i => i.severity === 'warning').length || 0;

    return (
      <div key={resourceKey} className="bg-dark-800 rounded border border-dark-700">
        <button
          onClick={() => toggleResource(resourceKey)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-dark-750"
        >
          <div className="flex items-center gap-2 min-w-0">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-dark-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-dark-500 flex-shrink-0" />
            )}
            {status === 'error' ? (
              <ShieldX className="w-4 h-4 text-red-500 flex-shrink-0" />
            ) : status === 'warning' ? (
              <ShieldAlert className="w-4 h-4 text-yellow-500 flex-shrink-0" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
            <div className="text-left min-w-0">
              <div className="text-sm text-dark-200 font-medium truncate">
                {resource.objectName}
              </div>
              <div className="text-[10px] text-dark-500 truncate flex items-center gap-1">
                <span>{resource.kind}</span>
                <span>•</span>
                {onNavigateToResource ? (
                  <button
                    onClick={(e) => handleNavigateToResource(e, resource.fileName)}
                    className="text-accent-primary hover:text-accent-primary/80 hover:underline flex items-center gap-0.5"
                    title="View in output"
                  >
                    {resource.fileName}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </button>
                ) : (
                  <span>{resource.fileName}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {errorCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded">
                {errorCount} error{errorCount > 1 ? 's' : ''}
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded">
                {warningCount} warning{warningCount > 1 ? 's' : ''}
              </span>
            )}
            {issueCount === 0 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded">
                Passed
              </span>
            )}
          </div>
        </button>
        {isExpanded && resource.issues && resource.issues.length > 0 && (
          <div className="border-t border-dark-700 p-2 space-y-1.5">
            {resource.issues.map((issue, idx) => renderIssue(issue, idx))}
          </div>
        )}
        {isExpanded && (!resource.issues || resource.issues.length === 0) && (
          <div className="border-t border-dark-700 p-3 text-center text-xs text-dark-500">
            No issues found for this resource
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={clsx('h-full bg-dark-850 flex flex-col', className)}>
      {/* Header */}
      <div className="p-3 md:p-2 border-b border-dark-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs md:text-[10px] font-semibold text-dark-400 uppercase tracking-wider flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Security Analysis
          </h2>
          <button
            onClick={onScan}
            disabled={isScanning || !hasManifests}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 md:px-1.5 md:py-0.5 text-xs md:text-[10px] rounded transition-colors',
              isScanning || !hasManifests
                ? 'text-dark-600 cursor-not-allowed'
                : 'text-dark-400 hover:text-dark-200 hover:bg-dark-700'
            )}
            title={!hasManifests ? 'Render manifests first' : 'Scan for security issues'}
          >
            {isScanning ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            <span>{isScanning ? 'Scanning...' : 'Scan'}</span>
          </button>
        </div>

        {/* Summary */}
        {scanResult && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            <div className="bg-dark-800 rounded p-2 text-center">
              <div className="text-lg font-bold text-dark-200">
                {scanResult.summary.totalResources}
              </div>
              <div className="text-[9px] text-dark-500 uppercase">Resources</div>
            </div>
            <div className="bg-dark-800 rounded p-2 text-center">
              <div className="text-lg font-bold text-green-500">
                {scanResult.summary.passedCount}
              </div>
              <div className="text-[9px] text-dark-500 uppercase">Passed</div>
            </div>
            <div className="bg-dark-800 rounded p-2 text-center">
              <div className="text-lg font-bold text-yellow-500">
                {scanResult.summary.warningCount}
              </div>
              <div className="text-[9px] text-dark-500 uppercase">Warnings</div>
            </div>
            <div className="bg-dark-800 rounded p-2 text-center">
              <div className="text-lg font-bold text-red-500">
                {scanResult.summary.criticalCount}
              </div>
              <div className="text-[9px] text-dark-500 uppercase">Critical</div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide-mobile">
        {isScanning ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <Loader2 className="w-6 h-6 text-dark-400 animate-spin" />
            <p className="text-sm text-dark-500">Scanning manifests...</p>
          </div>
        ) : scanResult ? (
          scanResult.resources.length > 0 ? (
            <div className="p-2 space-y-2">
              {scanResult.resources.map((resource, index) =>
                renderResource(resource, index)
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-2 p-4">
              <Info className="w-6 h-6 text-dark-500" />
              <p className="text-sm text-dark-500 text-center">
                No resources found to scan.
              </p>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 p-4">
            <Shield className="w-8 h-8 text-dark-600" />
            <p className="text-sm text-dark-500 text-center">
              {hasManifests
                ? 'Click "Scan" to analyze'
                : 'Render manifests first to enable scanning'}
            </p>
            <p className="text-[10px] text-dark-600 text-center max-w-[200px]">
              Powered by KubeLinter
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {scanResult && (
        <div className="border-t border-dark-700 p-2 md:p-1.5">
          <div className="flex items-center justify-between text-xs md:text-[10px] text-dark-400">
            <span>{scanResult.summary.totalIssues} issue(s) found</span>
            <span>{scanResult.scanTimeMs}ms</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const SecurityPanel = memo(SecurityPanelComponent);
